import * as _ from "lodash";
import { createContext, FC, PropsWithChildren, useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import {
  Enterprise as App,
  EnterpriseAppState,
  Invitation,
  InvitationList,
  InvitationType,
  ReferralEntity,
  ReferralStatus,
  Session
} from "@ctra/api";

import { CookieKeys, Cookies, isFulfilled, isPending } from "@ctra/utils";
import { useFarmList } from "@farms";
import { useSpinner } from "@ctra/components";
import { Enterprise as Content, useTranslation } from "@ctra/i18n";

interface ContextType {
  pending: Array<ReferralEntity>;
  handleAccept: (code: string) => void;
  meta: {
    isLoading: boolean;
  };
}

const DefaultContext = createContext<ContextType>({
  pending: [],
  handleAccept: _.noop,
  meta: {
    isLoading: true
  }
});

/**
 * Invitation context
 * @param children
 * @param params
 * @private
 */
const _InvitationContextProvider: FC<PropsWithChildren<Record<string, unknown>>> = ({ children }) => {
  const { farmList, isLoading: farmListLoading } = useFarmList();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const {
    api: { toggle }
  } = useSpinner();

  const {
    ui: { loaders }
  } = Content;

  const hasFarms = !_.isEmpty(farmList);
  const invitationCode = Cookies.get(CookieKeys.invitationCode);

  /**
   * Get the received invitations
   * @type {InvitationList}
   */
  const invitationList = useSelector<EnterpriseAppState, InvitationList>((state) =>
    App.entities.getReferrals(state, { variant: InvitationType.received })
  );

  /**
   * Filter and get only the invitations not accepted yet
   * @type {ReferralEntity[]}
   */
  const pending = _.filter(invitationList, ({ status }) => !_.isEqual(status, ReferralStatus.accepted));

  /**
   * Tell is an invitation is being accepted
   * @type {boolean}
   */
  const isLoading = useSelector<EnterpriseAppState, boolean>((state) =>
    isPending(state, Invitation.types.ACCEPT_INVITATION)
  );

  /**
   * Tell if an invitation is accepted
   * @type {boolean}
   */
  const accepted = useSelector<EnterpriseAppState, boolean>((state) =>
    isFulfilled(state, Invitation.types.ACCEPT_INVITATION)
  );

  /**
   * Accept the invitation
   * @param {string} code
   */
  const handleAccept = (code: string) => {
    dispatch(Invitation.actions.acceptInvitation.start(code));
  };

  /**
   * Fetch the invitation list or accept the invitation
   */
  useEffect(() => {
    if (!hasFarms && !farmListLoading && !invitationCode) {
      dispatch(Invitation.actions.fetchInvitationList.start());
    }

    if (invitationCode) {
      toggle("invitationContext", true, {
        size: "large",
        tip: t<string>(loaders.acceptInvite)
      });

      handleAccept(invitationCode);
      Cookies.remove(CookieKeys.invitationCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFarms, invitationCode]);

  /**
   * If invitation is accepted, remove any params (invitation)
   * flush and reload
   */
  useEffect(() => {
    if (accepted) {
      App.history.replace(pathname);
      App.persistor.flush();
      dispatch(Session.actions.reset());
      toggle("invitationContext", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accepted]);

  return (
    <DefaultContext.Provider
      value={{
        pending,
        handleAccept,
        meta: { isLoading }
      }}
    >
      {children}
    </DefaultContext.Provider>
  );
};

export const InvitationContext = {
  Provider: _InvitationContextProvider,
  Consumer: DefaultContext.Consumer
};

/**
 * Make a hook to access the context in FCs
 */
export const useInvitation = (): ContextType => useContext(DefaultContext);
